/**
 * AttorneyPackageView - Secure Package Access Page
 * Allows attorneys to view their case package via secure link
 * Phase 6: Report Pipeline & Delivery
 */

import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Scale,
  FileText,
  DollarSign,
  Shield,
  Download,
  AlertCircle,
  Loader2,
  CheckCircle2,
  TrendingUp,
  ChevronRight,
  ChevronDown,
  Clock,
  Users,
  Lock,
  ExternalLink
} from 'lucide-react';
import { FinancialValue } from '@/components/shared/FinancialValue';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

interface PackageData {
  id: string;
  report_id: string;
  generated_at: string;
  status: string;
  case_name: string;
  client_name: string;
  event_type: string;
  executive_summary?: string | null;
  settlement_calculation: {
    total: number;
    breakdown?: Record<string, number>;
    methodology?: string;
    confidence?: number;
    confidence_interval_low?: number;
    confidence_interval_high?: number;
  };
  case_strength_report: {
    score: number;
    vulnerabilities_total?: number;
    vulnerabilities_addressed?: number;
    evidence_completeness?: number;
    blocking_issues?: string[];
  };
  vulnerability_matrix: Array<{
    vulnerability_id: string;
    vulnerability: any;
    attack_vector: string;
    strategy_a?: any;
    strategy_b?: any;
    strategy_c?: any;
    recommended?: string;
  }>;
  document_index: Array<{
    doc_id: string;
    filename: string;
    doc_type: string;
    uploaded_at: string;
    page_count?: number;
  }>;
  confidence_scores: {
    data_extraction?: number;
    forensic_calculation?: number;
    case_strength?: number;
    overall?: number;
  };
  methodology_notes?: string | null;
  total_documents: number;
  total_transactions: number;
  pdf_url?: string;
  version: number;
}

export default function AttorneyPackageView() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [packageData, setPackageData] = useState<PackageData | null>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    summary: true,
    settlement: true,
    strength: false,
    defense: false,
    documents: false,
    methodology: false
  });
  const [downloading, setDownloading] = useState(false);
  const [downloadingDocx, setDownloadingDocx] = useState(false);

  useEffect(() => {
    if (token) {
      fetchPackage();
    }
  }, [token]);

  const fetchPackage = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/packages/access/${token}/package`);

      if (!response.ok) {
        if (response.status === 401) {
          setError('This link has expired or is invalid. Please contact your The Figure Co. representative.');
        } else {
          setError('Unable to load the package. Please try again.');
        }
        return;
      }

      const data = await response.json();
      setPackageData(data);
    } catch (err) {
      console.error('Error fetching package:', err);
      setError('Unable to connect to the server. Please check your internet connection.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!token || downloading) return;

    setDownloading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/packages/access/${token}/download`, {
        method: 'POST'
      });

      const data = await response.json();
      if (data.pdf_url) {
        window.open(data.pdf_url, '_blank');
      } else {
        toast.error('PDF download is not available for this package.');
      }
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Failed to download the package.');
    } finally {
      setDownloading(false);
    }
  };

  const handleDocxDownload = async () => {
    if (!packageData || downloadingDocx) return;

    setDownloadingDocx(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/v1/packages/${packageData.id}/docx`);

      if (!response.ok) {
        throw new Error('DOCX download failed');
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get('content-disposition');
      let filename = `Attorney_Package_${packageData.case_name || packageData.id}.docx`;

      if (contentDisposition) {
        const match = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (match && match[1]) {
          filename = match[1].replace(/['"]/g, '');
        }
      }

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('DOCX download error:', err);
      toast.error('Failed to download the DOCX package.');
    } finally {
      setDownloadingDocx(false);
    }
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--color-bg-base)' }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin mx-auto" style={{ color: 'var(--color-brand)' }} />
          <p className="mt-4 cs-body">Loading your package...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--color-bg-base)' }}>
        <div className="w-full max-w-md text-center" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 32 }}>
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
            <Lock className="w-8 h-8" style={{ color: 'var(--color-brand)' }} />
          </div>
          <h2 className="cs-h2 mb-2">Access Error</h2>
          <p className="cs-body mb-6">{error}</p>
          <div className="cs-meta">
            <p>If you believe this is an error, please contact:</p>
            <a href="mailto:support@claimsync.io" className="hover:underline" style={{ color: 'var(--color-needs-human-text)' }}>support@claimsync.io</a>
          </div>
        </div>
      </div>
    );
  }

  if (!packageData) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'var(--color-bg-base)' }}>
        <div className="w-full max-w-md text-center" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 32 }}>
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)' }}>
            <AlertCircle className="w-8 h-8" style={{ color: 'var(--color-brand)' }} />
          </div>
          <h2 className="cs-h2 mb-2">Attorney package unavailable</h2>
        </div>
      </div>
    );
  }

  const { case_strength_report, settlement_calculation, vulnerability_matrix, document_index } = packageData;

  return (
    <div className="min-h-screen" style={{ background: 'var(--color-bg-base)' }}>
      {/* Header */}
      <header className="sticky top-0 z-10" style={{ background: 'var(--color-bg-surface)', borderBottom: '1px solid var(--color-border)' }}>
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-brand)' }}>
              <Scale className="w-5 h-5" style={{ color: 'var(--color-on-brand)' }} />
            </div>
            <div>
              <h1 className="cs-h3">The Figure Co.</h1>
              <p className="cs-meta">Attorney Package</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleDownload}
              disabled={downloading}
              className="cs-body flex items-center gap-2 transition disabled:opacity-50"
              style={{ padding: '8px 16px', borderRadius: 10, background: 'var(--color-brand)', color: 'var(--color-on-brand)', fontWeight: 500 }}
            >
              {downloading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Download className="w-4 h-4" />
              )}
              Download PDF
            </button>
            <button
              onClick={handleDocxDownload}
              disabled={downloadingDocx}
              className="cs-body flex items-center gap-2 transition disabled:opacity-50"
              style={{ padding: '8px 16px', borderRadius: 10, background: 'var(--color-bg-elevated)', border: '1px solid var(--color-border)', color: 'var(--color-text-primary)', fontWeight: 500 }}
            >
              {downloadingDocx ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <FileText className="w-4 h-4" />
              )}
              Download DOCX
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8">
        {/* Case Header */}
        <div className="mb-6" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14, padding: 24 }}>
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div>
              <h2 className="cs-display">{packageData.case_name}</h2>
              <p className="cs-body">{packageData.event_type}</p>
            </div>
            <div className="cs-chip flex items-center gap-2" style={{ padding: '4px 12px', borderRadius: 9999, background: 'transparent', color: 'var(--color-verified)' }}>
              <CheckCircle2 className="w-4 h-4" />
              Analysis Complete
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div style={{ background: 'var(--color-bg-elevated)', borderRadius: 12, padding: 16 }}>
              <p className="cs-label mb-1">Calculated Loss</p>
              <p className="cs-figure-lg">
                <FinancialValue value={settlement_calculation?.total} cents />
              </p>
            </div>
            <div style={{ background: 'var(--color-bg-elevated)', borderRadius: 12, padding: 16 }}>
              <p className="cs-label mb-1">Case Strength</p>
              <div className="flex items-center gap-2">
                <p className="cs-figure-lg">
                  {case_strength_report?.score || 0}/100
                </p>
                <TrendingUp className="w-5 h-5" style={{ color: 'var(--color-verified)' }} />
              </div>
            </div>
            <div style={{ background: 'var(--color-bg-elevated)', borderRadius: 12, padding: 16 }}>
              <p className="cs-label mb-1">Defense Points</p>
              <p className="cs-figure-lg" style={{ color: 'var(--color-needs-human-text)' }}>
                {vulnerability_matrix?.length || 0}
              </p>
            </div>
            <div style={{ background: 'var(--color-bg-elevated)', borderRadius: 12, padding: 16 }}>
              <p className="cs-label mb-1">Documents</p>
              <p className="cs-figure-lg">
                {packageData.total_documents}
              </p>
            </div>
          </div>
        </div>

        {/* Executive Summary */}
        <Section
          title="Executive Summary"
          icon={<FileText className="w-5 h-5" />}
          expanded={expandedSections.summary}
          onToggle={() => toggleSection('summary')}
        >
          <div className="max-w-none">
            {(packageData.executive_summary ?? '').split('\n').map((paragraph, index) => (
              <p key={index} className="cs-body">{paragraph}</p>
            ))}
          </div>
        </Section>

        {/* Settlement Calculation */}
        <Section
          title="Settlement Calculation"
          icon={<DollarSign className="w-5 h-5" />}
          expanded={expandedSections.settlement}
          onToggle={() => toggleSection('settlement')}
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 12 }}>
              <span className="cs-body" style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>Total Calculated Loss</span>
              <span className="cs-figure-lg" style={{ color: 'var(--color-needs-human-text)' }}>
                <FinancialValue value={settlement_calculation?.total} cents />
              </span>
            </div>

            {settlement_calculation?.breakdown && Object.keys(settlement_calculation.breakdown).length > 0 && (
              <div className="divide-y divide-[var(--color-border-faint)]" style={{ border: '1px solid var(--color-border)', borderRadius: 12 }}>
                {Object.entries(settlement_calculation.breakdown).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between p-3">
                    <span className="cs-body capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="cs-figure" style={{ color: 'var(--color-text-primary)' }}><FinancialValue value={value} cents /></span>
                  </div>
                ))}
              </div>
            )}

            {settlement_calculation?.confidence_interval_low && settlement_calculation?.confidence_interval_high && (
              <div className="p-4" style={{ background: 'var(--color-bg-elevated)', borderRadius: 12 }}>
                <p className="cs-label mb-2">Confidence Interval (95%)</p>
                <p className="cs-figure">
                  <FinancialValue value={settlement_calculation.confidence_interval_low} cents /> — <FinancialValue value={settlement_calculation.confidence_interval_high} cents />
                </p>
              </div>
            )}

            <div className="cs-meta">
              <p>Methodology: {settlement_calculation?.methodology || 'BI'}</p>
              <p>Calculation Confidence: {((settlement_calculation?.confidence || 0) * 100).toFixed(0)}%</p>
            </div>
          </div>
        </Section>

        {/* Case Strength */}
        <Section
          title="Case Strength Assessment"
          icon={<TrendingUp className="w-5 h-5" />}
          expanded={expandedSections.strength}
          onToggle={() => toggleSection('strength')}
        >
          <div className="space-y-4">
            {/* Score Gauge */}
            <div className="flex items-center gap-6">
              <div className="relative w-32 h-32">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                  <circle
                    cx="50"
                    cy="50"
                    r="42"
                    stroke="var(--color-border)"
                    strokeWidth="12"
                    fill="none"
                  />
                  <circle
                    cx="50"
                    cy="50"
                    r="42"
                    stroke="var(--color-brand)"
                    strokeWidth="12"
                    fill="none"
                    strokeDasharray={`${(case_strength_report?.score || 0) * 2.64} 264`}
                    strokeLinecap="round"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="cs-figure-lg">{case_strength_report?.score || 0}</span>
                </div>
              </div>
              <div>
                <p className="cs-h3">
                  {(case_strength_report?.score || 0) >= 90 ? 'Excellent' :
                   (case_strength_report?.score || 0) >= 75 ? 'Strong' :
                   (case_strength_report?.score || 0) >= 60 ? 'Moderate' : 'Needs Improvement'}
                </p>
                <p className="cs-meta">Overall Case Strength</p>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4" style={{ background: 'var(--color-bg-elevated)', borderRadius: 12 }}>
                <p className="cs-label">Evidence Completeness</p>
                <p className="cs-figure" style={{ fontSize: '1rem', color: 'var(--color-text-primary)' }}>
                  {((case_strength_report?.evidence_completeness || 0) * 100).toFixed(0)}%
                </p>
              </div>
              <div className="p-4" style={{ background: 'var(--color-bg-elevated)', borderRadius: 12 }}>
                <p className="cs-label">Vulnerabilities Addressed</p>
                <p className="cs-figure" style={{ fontSize: '1rem', color: 'var(--color-text-primary)' }}>
                  {case_strength_report?.vulnerabilities_addressed || 0} / {case_strength_report?.vulnerabilities_total || 0}
                </p>
              </div>
            </div>

            {case_strength_report?.blocking_issues && case_strength_report.blocking_issues.length > 0 && (
              <div className="p-4" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', borderRadius: 12 }}>
                <p className="cs-body mb-2" style={{ fontWeight: 500, color: 'var(--color-needs-human-text)' }}>Blocking Issues</p>
                <ul className="space-y-1">
                  {case_strength_report.blocking_issues.map((issue, index) => (
                    <li key={index} className="cs-body flex items-start gap-2" style={{ color: 'var(--color-needs-human-text)' }}>
                      <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                      {issue}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </Section>

        {/* Defense Matrix */}
        <Section
          title="Defense Matrix"
          icon={<Shield className="w-5 h-5" />}
          expanded={expandedSections.defense}
          onToggle={() => toggleSection('defense')}
        >
          {vulnerability_matrix.length > 0 ? (
            <div className="space-y-4">
              {vulnerability_matrix.map((item, index) => (
                <div key={index} className="overflow-hidden" style={{ border: '1px solid var(--color-border)', borderRadius: 12 }}>
                  <div className="p-4" style={{ background: 'var(--color-bg-elevated)', borderBottom: '1px solid var(--color-border-sub)' }}>
                    <p className="cs-body" style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>
                      {typeof item.attack_vector === 'string' ? item.attack_vector : item.vulnerability?.title || `Vulnerability ${index + 1}`}
                    </p>
                  </div>
                  <div className="p-4 space-y-3">
                    {item.strategy_a && (
                      <StrategyCard
                        label="A"
                        strategy={item.strategy_a}
                        recommended={item.recommended === 'A'}
                      />
                    )}
                    {item.strategy_b && (
                      <StrategyCard
                        label="B"
                        strategy={item.strategy_b}
                        recommended={item.recommended === 'B'}
                      />
                    )}
                    {item.strategy_c && (
                      <StrategyCard
                        label="C"
                        strategy={item.strategy_c}
                        recommended={item.recommended === 'C'}
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="cs-meta text-center py-8">No defense points identified.</p>
          )}
        </Section>

        {/* Document Index */}
        <Section
          title={`Document Index (${document_index.length})`}
          icon={<FileText className="w-5 h-5" />}
          expanded={expandedSections.documents}
          onToggle={() => toggleSection('documents')}
        >
          {document_index.length > 0 ? (
            <div className="divide-y divide-[var(--color-border-faint)]" style={{ border: '1px solid var(--color-border)', borderRadius: 12 }}>
              {document_index.map((doc, index) => (
                <div key={index} className="flex items-center justify-between p-3 hover:bg-[var(--color-bg-base)]">
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
                    <div>
                      <p className="cs-body" style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{doc.filename}</p>
                      <p className="cs-meta">{doc.doc_type}</p>
                    </div>
                  </div>
                  <div className="text-right cs-meta">
                    {doc.page_count && <p>{doc.page_count} pages</p>}
                    <p>{formatDate(doc.uploaded_at)}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="cs-meta text-center py-8">No documents indexed.</p>
          )}
        </Section>

        {/* Methodology Notes */}
        <Section
          title="Methodology Notes"
          icon={<Users className="w-5 h-5" />}
          expanded={expandedSections.methodology}
          onToggle={() => toggleSection('methodology')}
        >
          <div className="max-w-none">
            {(packageData.methodology_notes ?? '').split('\n').map((line, index) => (
              <p key={index} className="cs-body">{line}</p>
            ))}
          </div>
        </Section>

        {/* Footer */}
        <div className="mt-8 text-center cs-meta">
          <p>Package ID: {packageData.id}</p>
          <p>Generated: {formatDate(packageData.generated_at)} • Version {packageData.version}</p>
          <p className="mt-2">© {new Date().getFullYear()} The Figure Co. All rights reserved.</p>
        </div>
      </main>
    </div>
  );
}

// Collapsible Section Component
interface SectionProps {
  title: string;
  icon: React.ReactNode;
  expanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function Section({ title, icon, expanded, onToggle, children }: SectionProps) {
  return (
    <div className="mb-4 overflow-hidden" style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 14 }}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 transition hover:bg-[var(--color-bg-hover)]"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'var(--color-needs-human-tint)', border: '1px solid var(--color-needs-human-tint-bd)', color: 'var(--color-brand)' }}>
            {icon}
          </div>
          <span className="cs-h3">{title}</span>
        </div>
        {expanded ? (
          <ChevronDown className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
        ) : (
          <ChevronRight className="w-5 h-5" style={{ color: 'var(--color-text-muted)' }} />
        )}
      </button>
      {expanded && (
        <div className="px-4 pb-4">
          {children}
        </div>
      )}
    </div>
  );
}

// Strategy Card Component
interface StrategyCardProps {
  label: string;
  strategy: {
    name?: string;
    description?: string;
    strength?: number;
    risk_level?: string;
    counter_arguments?: string[];
  };
  recommended: boolean;
}

function StrategyCard({ label, strategy, recommended }: StrategyCardProps) {
  return (
    <div
      className="p-3 rounded-lg"
      style={recommended
        ? { border: '1px solid var(--color-needs-human-tint-bd)', background: 'var(--color-needs-human-tint)' }
        : { border: '1px solid var(--color-border)' }}
    >
      <div className="flex items-center gap-2 mb-2">
        <span
          className="cs-chip w-6 h-6 rounded-full flex items-center justify-center"
          style={recommended
            ? { background: 'var(--color-brand)', color: 'var(--color-on-brand)' }
            : { background: 'var(--color-bg-elevated)', color: 'var(--color-text-secondary)' }}
        >
          {label}
        </span>
        <span className="cs-body" style={{ fontWeight: 500, color: 'var(--color-text-primary)' }}>{strategy.name || `Strategy ${label}`}</span>
        {recommended && (
          <span className="cs-chip ml-auto px-2 py-0.5 rounded-full" style={{ background: 'var(--color-brand)', color: 'var(--color-on-brand)' }}>
            Recommended
          </span>
        )}
      </div>
      <p className="cs-meta">{strategy.description || 'No description available.'}</p>
      {strategy.counter_arguments && strategy.counter_arguments.length > 0 && (
        <div className="mt-2">
          <p className="cs-label">Counter-argument:</p>
          <p className="cs-meta">{strategy.counter_arguments[0]}</p>
        </div>
      )}
      <div className="mt-2 flex items-center gap-4 cs-meta">
        {strategy.strength !== undefined && (
          <span>Strength: {strategy.strength}/10</span>
        )}
        {strategy.risk_level && (
          <span
            className="capitalize"
            style={{
              color: strategy.risk_level === 'low' ? 'var(--color-verified)' :
                strategy.risk_level === 'high' ? 'var(--color-needs-human-text)' : 'var(--color-text-muted)'
            }}
          >
            {strategy.risk_level} risk
          </span>
        )}
      </div>
    </div>
  );
}
